import { useRef, type Ref, type RefObject } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { checkboxClass, Field, FieldError, HelpTip, labelClass, NativeSelect } from "@/components/admin/cards";
import { DATE_FORMATS } from "./bookingLink";
import { MAX_CUSTOM_FIELDS, MAX_FIELD_LABEL, ReservationFieldSetting, ReservationMode, templatePlaceholders } from "./reservationTypes";
import {
  addsEmail,
  CANCEL_REQUEST_FIELD_LABELS,
  CANCEL_REQUEST_FIELDS,
  CANCEL_REQUEST_MODE_OPTIONS,
  CancelRequest,
  CancelRequestErrors,
  CancelRequestField,
  cancelFieldLabel,
  cancelRequestCustomFields,
  customFieldId,
} from "./cancelRequest";

/** How guests cancel: the plain Cancel booking link (no questions) or one of the cancel request modes. */
export type CancelWay = "link" | ReservationMode;

interface CancelRequestEditorProps {
  value: CancelRequest | null;
  errors: CancelRequestErrors & { cancelRequest?: string; cancelUrl?: string };
  /** Name of the plain-link choice, which depends on the booking settings. */
  linkLabel: string;
  /** The Cancel Booking URL (``content.url``), or ``null`` when the chatbot builds the link itself. */
  cancelUrl: string | null;
  onCancelUrl: (url: string) => void;
  onChoose: (way: CancelWay) => void;
  onChange: (value: CancelRequest) => void;
  cancelUrlRef?: Ref<HTMLInputElement>;
  urlRef?: RefObject<HTMLInputElement | null>;
  emailRef?: Ref<HTMLInputElement>;
  fieldsRef?: Ref<HTMLDivElement>;
  errorRef?: Ref<HTMLDivElement>;
}

/** Everything about cancelling: the plain Cancel booking link or the "cancel my booking" request flow (``content.cancelRequest``). */
const CancelRequestEditor = ({
  value, errors, linkLabel, cancelUrl, onCancelUrl, onChoose, onChange, cancelUrlRef, urlRef, emailRef, fieldsRef, errorRef,
}: CancelRequestEditorProps) => {
  const ownUrlRef = useRef<HTMLInputElement>(null);
  const urlInput = urlRef ?? ownUrlRef;
  const on = Boolean(value?.enabled);
  const way: CancelWay = on && value ? value.mode : "link";
  const update = (patch: Partial<CancelRequest>) => value && onChange({ ...value, ...patch });

  const builtin = (id: CancelRequestField) => value?.fields.find((field) => !field.custom && field.id === id);
  const setBuiltin = (id: CancelRequestField, field: ReservationFieldSetting | null) => {
    if (!value) return;
    const builtins = CANCEL_REQUEST_FIELDS.flatMap((key) => {
      if (key === id) return field ? [field] : [];
      const existing = builtin(key);
      return existing ? [existing] : [];
    });
    update({ fields: [...builtins, ...cancelRequestCustomFields(value)] });
  };

  const setCustom = (customIndex: number, patch: Partial<ReservationFieldSetting> | null) => {
    if (!value) return;
    let seen = -1;
    const fields = value.fields.flatMap((field, index) => {
      if (!field.custom || ++seen !== customIndex) return [field];
      if (!patch) return [];
      if (patch.label === undefined) return [{ ...field, ...patch }];
      const otherIds = value.fields.filter((_, i) => i !== index).map((other) => other.id);
      return [{ ...field, ...patch, id: customFieldId(field, patch.label, otherIds) }];
    });
    update({ fields });
  };

  const insertPlaceholder = (id: string) => {
    if (!value) return;
    const input = urlInput.current;
    const text = value.urlTemplate;
    const start = input?.selectionStart ?? text.length;
    const end = input?.selectionEnd ?? text.length;
    const placeholder = `{${id}}`;
    update({ urlTemplate: text.slice(0, start) + placeholder + text.slice(end) });
    window.setTimeout(() => {
      input?.focus();
      input?.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 0);
  };

  const custom = value ? cancelRequestCustomFields(value) : [];
  const used = value ? templatePlaceholders(value.urlTemplate) : [];
  const notPassed = value?.mode === "link_out" ? value.fields.filter((field) => field.id && !used.includes(field.id)) : [];

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="How guests cancel" htmlFor="cancelRequestMode"
          hint="The chatbot never cancels a booking itself. With &quot;Ask for details&quot;, it first asks the guest for the details below, then opens your cancel page or emails your team.">
          <NativeSelect id="cancelRequestMode" value={way} onChange={(e) => onChoose(e.target.value as CancelWay)}>
            <option value="link">{linkLabel}</option>
            {CANCEL_REQUEST_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </NativeSelect>
        </Field>
        {way === "link" && cancelUrl !== null && (
          <Field label="Cancel page link" htmlFor="cancelBookingUrl" error={errors.cancelUrl}
            hint="Where guests go to cancel or change a booking. Optional: leave it blank if you have no cancel page, and the chatbot won't show the Cancel booking button.">
            <Input id="cancelBookingUrl" type="url" ref={cancelUrlRef} placeholder="https://your-booking-engine.com/manage-booking"
              value={cancelUrl} onChange={(e) => onCancelUrl(e.target.value)} />
          </Field>
        )}
        {on && value && (value.mode === "link_out" ? (
          builtin("date") && (
            <Field label="Date format" htmlFor="cancelRequestDateFormat" hint="How the check-in date is written in the link.">
              <NativeSelect id="cancelRequestDateFormat" value={value.dateFormat}
                onChange={(e) => update({ dateFormat: e.target.value })}>
                {DATE_FORMATS.map((format) => <option key={format} value={format}>{format}</option>)}
              </NativeSelect>
            </Field>
          )
        ) : (
          <Field label="Hotel email" htmlFor="cancelRequestEmail" required error={errors.cancelRequestEmail}
            hint="Where the guest's cancel request is sent.">
            <Input id="cancelRequestEmail" type="email" ref={emailRef} placeholder="reservations@your-property.com"
              value={value.enquiryEmail} onChange={(e) => update({ enquiryEmail: e.target.value })} />
          </Field>
        ))}
      </div>

      {on && value && (
        <>
          {value.mode === "link_out" && (
            <Field label="Cancel page link" htmlFor="cancelRequestUrl" required error={errors.cancelRequestUrl}
              hint="The chatbot fills in the guest's answers where the link has a {detail}. Click a detail to add it to the link.">
              <Input id="cancelRequestUrl" ref={urlInput} placeholder="https://your-booking-engine.com/cancel?ref={booking_id}"
                value={value.urlTemplate} onChange={(e) => update({ urlTemplate: e.target.value })} />
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Add to link:</span>
                {value.fields.filter((field) => field.id).map((field) => (
                  <button key={field.id} type="button" onClick={() => insertPlaceholder(field.id)}
                    aria-label={`Add ${cancelFieldLabel(field)} to the link`}
                    className="inline-flex h-6 items-center gap-1 rounded-full border-0 bg-primary/10 px-2.5 text-xs font-medium text-primary hover:bg-primary/20 focus-visible:outline-2 focus-visible:outline-ring">
                    <Plus className="size-3" aria-hidden="true" />{cancelFieldLabel(field)}
                  </button>
                ))}
              </div>
              {!errors.cancelRequestUrl && value.urlTemplate.trim() && notPassed.length > 0 && (
                <p className="text-xs text-warning-700" role="status">
                  Not in the link: {notPassed.map(cancelFieldLabel).join(", ")}.
                </p>
              )}
            </Field>
          )}

          <div className="flex flex-col gap-2" ref={fieldsRef}>
            <div className="flex items-center gap-1">
              <span className={labelClass}>Details the chatbot asks for</span>
              <HelpTip label="Details the chatbot asks for">
                {addsEmail(value)
                  ? "The chatbot also asks for the guest's email so your team can reply."
                  : "Untick a detail to skip it; untick Required to let guests skip it."}
              </HelpTip>
            </div>
            <div className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
              {CANCEL_REQUEST_FIELDS.map((id) => {
                const field = builtin(id);
                const label = CANCEL_REQUEST_FIELD_LABELS[id];
                return (
                  <div key={id} className="flex items-center gap-4 text-sm">
                    <span className="flex min-w-32 items-center gap-2">
                      <input id={`cancelRequestField_${id}`} type="checkbox" className={checkboxClass}
                        checked={Boolean(field)} onChange={(e) => setBuiltin(id, e.target.checked ? { id, required: true } : null)} />
                      <label htmlFor={`cancelRequestField_${id}`}>{label}</label>
                    </span>
                    <span className="flex items-center gap-2">
                      <input id={`cancelRequestField_${id}_required`} type="checkbox" className={checkboxClass}
                        aria-label={`${label} required`} disabled={!field} checked={Boolean(field?.required)}
                        onChange={(e) => field && setBuiltin(id, { ...field, required: e.target.checked })} />
                      <label className="text-muted-foreground" htmlFor={`cancelRequestField_${id}_required`} aria-hidden="true">Required</label>
                    </span>
                  </div>
                );
              })}
            </div>
            {custom.map((field, customIndex) => (
              <div key={customIndex} className="flex items-center gap-3">
                <Input className="h-8 max-w-80" maxLength={MAX_FIELD_LABEL}
                  aria-label={`Extra detail ${customIndex + 1} name`} placeholder="e.g. Room number"
                  value={field.label || ""} onChange={(e) => setCustom(customIndex, { label: e.target.value })} />
                <span className="flex items-center gap-2 text-sm">
                  <input id={`cancelRequestCustom${customIndex}Required`} type="checkbox" className={checkboxClass}
                    aria-label={`Extra detail ${customIndex + 1} required`} checked={field.required}
                    onChange={(e) => setCustom(customIndex, { required: e.target.checked })} />
                  <label htmlFor={`cancelRequestCustom${customIndex}Required`} aria-hidden="true">Required</label>
                </span>
                <Button type="button" variant="outline" size="icon" className="size-8 text-destructive"
                  aria-label={`Remove extra detail ${field.label || customIndex + 1}`} onClick={() => setCustom(customIndex, null)}>
                  <X aria-hidden="true" />
                </Button>
              </div>
            ))}
            {custom.length < MAX_CUSTOM_FIELDS && (
              <div>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => update({ fields: [...value.fields, { id: "", label: "", type: "text", required: true, custom: true }] })}>
                  <Plus aria-hidden="true" />Add extra detail
                </Button>
              </div>
            )}
            <FieldError message={errors.cancelRequestFields} />
          </div>
        </>
      )}
      <div ref={errorRef} tabIndex={-1} className="outline-none">
        <FieldError message={errors.cancelRequest} />
      </div>
    </div>
  );
};

export default CancelRequestEditor;
